import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const EMAIL = "contact@sysbrains.com";

export default function Contact() {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
        } catch {
            const el = document.createElement("textarea");
            el.value = EMAIL; document.body.appendChild(el); el.select();
            document.execCommand("copy"); document.body.removeChild(el);
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative py-32 md:py-48 overflow-hidden">
            <div className="mx-auto max-w-[1480px] px-6 sm:px-12 md:px-20">
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-15%" }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="mb-16 flex items-baseline gap-6 border-b border-sb pb-6"
                >
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-sb-mint">04 · Contact</span>
                    <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">/ An invitation</span>
                    <span className="ml-auto hidden md:inline font-mono text-[10px] uppercase tracking-[0.24em] text-sb-muted">contact@sysbrains.com</span>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.8, ease: EASE }}
                        className="md:col-span-7"
                    >
                        <h2 className="balance text-[clamp(1.875rem,3.8vw,3.5rem)] font-medium leading-[1.08] tracking-[-0.025em]">
                            Let's build something{" "}
                            <span className="font-serif italic text-sb-mint">considered</span>.
                        </h2>
                        <p
                            data-testid="contact-availability"
                            className="mt-6 max-w-[54ch] text-base md:text-lg text-sb-soft font-light leading-relaxed"
                        >
                            Currently open to select collaborations and product partnerships.
                            Tell us what you're building. We'll reply in <span className="font-serif italic">one to two days</span>.
                        </p>
                    </motion.div>

                    <motion.aside
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-10%" }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.08 }}
                        className="md:col-span-4 md:col-start-9 border-sb border-l pl-6"
                    >
                        <div className="space-y-4">
                            {[
                                { k: "Response", v: "1–2 days" },
                                { k: "Engagements", v: "4–16 weeks" },
                                { k: "Slots", v: "2 / quarter" },
                                { k: "Stage", v: "Seed → Series B" },
                            ].map((r, i) => (
                                <div key={r.k} className="flex items-baseline justify-between gap-4 border-b border-sb pb-3">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-sb-muted">
                                        {String(i + 1).padStart(2, "0")} · {r.k}
                                    </span>
                                    <span className="text-sm text-sb">{r.v}</span>
                                </div>
                            ))}
                        </div>
                    </motion.aside>
                </div>

                {/* Massive email */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-10%" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
                    className="mt-20 md:mt-28"
                >
                    <a
                        href={`mailto:${EMAIL}?subject=Partnership%20inquiry%20%E2%80%94%20SysBrains`}
                        data-testid="contact-email-link"
                        className="group inline-flex flex-wrap items-baseline gap-3 md:gap-5"
                    >
                        <span className="block text-[clamp(1.75rem,5.2vw,5rem)] font-medium tracking-[-0.035em] leading-[1] text-sb transition-colors group-hover:text-sb-mint">
                            contact
                            <span className="font-serif italic text-sb-mint group-hover:text-sb transition-colors">@</span>
                            sysbrains
                            <span className="text-sb-muted">.com</span>
                        </span>
                        <span className="hidden md:inline-flex h-12 w-12 items-center justify-center rounded-full border-sb border text-sb-muted transition-all group-hover:border-sb-mint group-hover:text-sb-mint group-hover:-translate-y-1 group-hover:translate-x-1">
                            <svg width="16" height="16" viewBox="0 0 10 10" fill="none"><path d="M1 9L9 1M9 1H2M9 1V8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="mt-10 flex flex-wrap items-center gap-3"
                >
                    <button
                        type="button"
                        onClick={handleCopy}
                        data-testid="contact-copy-button"
                        aria-label="Copy email address"
                        className="group inline-flex items-center gap-2 rounded-full border-sb border px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] text-sb-soft transition-all hover:border-sb-mint hover:text-sb-mint focus-ring"
                    >
                        {copied ? <Check className="h-3.5 w-3.5 text-sb-mint" /> : <Copy className="h-3.5 w-3.5" />}
                        <span>{copied ? "Copied to clipboard" : "Copy email"}</span>
                    </button>

                    <a
                        href={`mailto:${EMAIL}?subject=Partnership%20inquiry%20%E2%80%94%20SysBrains`}
                        data-testid="contact-mailto-button"
                        className="group inline-flex items-center gap-2 rounded-full bg-sb-mint pl-4 pr-2 py-1.5 text-xs font-medium text-[#0B1220] transition-all"
                    >
                        <span>Send a message</span>
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0B1220] text-sb-mint transition-transform group-hover:rotate-45">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 9L9 1M9 1H2M9 1V8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                    </a>
                </motion.div>
            </div>

            <div className="pointer-events-none absolute -bottom-1/3 left-1/2 -translate-x-1/2 h-[55vmax] w-[55vmax] mint-glow opacity-50 -z-10" aria-hidden />
        </section>
    );
}
